import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from '../components/Layout';
import { getDocuments, createDocument, updateDocument, deleteDocument, getProjects, getTasks } from '../api';
import api from '../api';

if (typeof document !== 'undefined' && !document.getElementById('gpf-doc-editor-style')) {
  const style = document.createElement('style');
  style.id = 'gpf-doc-editor-style';
  style.textContent = `
    .gpf-doc-editor blockquote { border-left: 3px solid #e2e8f0; margin: 10px 0; padding: 4px 0 4px 16px; color: #64748b; }
    .gpf-doc-editor pre { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 14px; font-family: monospace; font-size: 13px; overflow-x: auto; }
    .gpf-doc-editor a { color: #2563eb; text-decoration: underline; }
    .gpf-doc-editor h1 { font-size: 26px; font-weight: 800; margin: 16px 0 8px; }
    .gpf-doc-editor h2 { font-size: 21px; font-weight: 700; margin: 14px 0 6px; }
    .gpf-doc-editor h3 { font-size: 17px; font-weight: 700; margin: 12px 0 6px; }
    .gpf-doc-editor hr { border: none; border-top: 1.5px solid #e2e8f0; margin: 16px 0; }
    .gpf-doc-editor ul, .gpf-doc-editor ol { padding-left: 24px; }
  `;
  document.head.appendChild(style);
}

const TOOLBAR = [
  { cmd: 'undo', icon: '↶', style: {}, title: 'Undo' },
  { cmd: 'redo', icon: '↷', style: {}, title: 'Redo' },
  { sep: true },
  { cmd: 'bold',           icon: 'B',  style: { fontWeight: 700 },           title: 'Bold' },
  { cmd: 'italic',         icon: 'I',  style: { fontStyle: 'italic' },        title: 'Italic' },
  { cmd: 'underline',      icon: 'U',  style: { textDecoration: 'underline' },title: 'Underline' },
  { cmd: 'strikeThrough',  icon: 'S̶', style: {}, title: 'Strikethrough' },
  { cmd: 'removeFormat',   icon: '✕',  style: {}, title: 'Clear Formatting' },
  { sep: true },
  { cmd: 'formatBlock', val: 'h1', icon: 'H1', style: { fontWeight: 700 }, title: 'Heading 1' },
  { cmd: 'formatBlock', val: 'h2', icon: 'H2', style: { fontWeight: 600 }, title: 'Heading 2' },
  { cmd: 'formatBlock', val: 'h3', icon: 'H3', style: { fontWeight: 600 }, title: 'Heading 3' },
  { cmd: 'formatBlock', val: 'p',  icon: '¶',  style: {},                  title: 'Paragraph' },
  { cmd: 'formatBlock', val: 'blockquote', icon: '"', style: { fontStyle: 'italic' }, title: 'Quote' },
  { cmd: 'formatBlock', val: 'pre', icon: '</>', style: { fontFamily: 'monospace', fontSize: 11 }, title: 'Code Block' },
  { sep: true },
  { cmd: 'insertUnorderedList', icon: '•',  style: {}, title: 'Bullet List' },
  { cmd: 'insertOrderedList',   icon: '1.', style: {}, title: 'Numbered List' },
  { cmd: 'indent',  icon: '→¶', style: {}, title: 'Indent' },
  { cmd: 'outdent', icon: '←¶', style: {}, title: 'Outdent' },
  { sep: true },
  { cmd: 'justifyLeft',   icon: '⬱', style: {}, title: 'Align Left' },
  { cmd: 'justifyCenter', icon: '≡', style: {}, title: 'Center' },
  { cmd: 'justifyRight',  icon: '⬰', style: {}, title: 'Align Right' },
  { cmd: 'justifyFull',   icon: '≣', style: {}, title: 'Justify' },
  { sep: true },
  { type: 'link',    icon: '🔗',  style: {}, title: 'Insert Link' },
  { cmd: 'unlink',   icon: '🔗✕', style: { fontSize: 10 }, title: 'Remove Link' },
  { cmd: 'insertHorizontalRule', icon: '—', style: {}, title: 'Horizontal Rule' },
  { sep: true },
  { type: 'color',     icon: 'A', style: { fontWeight: 700 }, title: 'Text Color' },
  { type: 'highlight', icon: '▧', style: {}, title: 'Highlight Color' },
];

export default function DocumentsListPage() {
  const editorRef   = useRef(null);
  const currentUser = JSON.parse(localStorage.getItem('gpf_user') || '{}');

  // Data
  const [docs,      setDocs]      = useState([]);
  const [media,     setMedia]     = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [allTasks,  setAllTasks]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [tab,       setTab]       = useState('all'); // all | created | uploaded

  // Editor
  const [editing,   setEditing]   = useState(null);
  const [title,     setTitle]     = useState('');
  const [projectId, setProjectId] = useState('');
  const [taskId,    setTaskId]    = useState('');
  const [tasks,     setTasks]     = useState([]);
  const [saving,    setSaving]    = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [saveError,    setSaveError]    = useState('');
  const [lastSaved,    setLastSaved]    = useState(null);

  // Upload modal state
  const [showUpload,   setShowUpload]   = useState(false);
  const [uploadFile,   setUploadFile]   = useState(null);
  const [uploadProject,setUploadProject]= useState('');
  const [uploadTask,   setUploadTask]   = useState('');
  const [uploadTasks,  setUploadTasks]  = useState([]);
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState('');

  const fetchAll = async () => {
    try {
      const [dRes, mRes, pRes, tRes] = await Promise.allSettled([
        getDocuments(),
        api.get('/wp/v2/media?per_page=100&orderby=date&order=desc'),
        getProjects(),
        api.get('/wp/v2/tasks?per_page=100'),
      ]);
      if (dRes.status === 'fulfilled') setDocs(dRes.value.data);
      if (mRes.status === 'fulfilled') setMedia(mRes.value.data);
      if (pRes.status === 'fulfilled') setProjects(pRes.value.data);
      if (tRes.status === 'fulfilled') setAllTasks(tRes.value.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  useEffect(() => {
    if (projectId) {
      getTasks(projectId).then(res => setTasks(res.data)).catch(console.error);
    } else {
      setTasks([]); setTaskId('');
    }
  }, [projectId]);

  // ── Helpers ──
  // Load tasks when upload project changes
  const handleUploadProjectChange = async (projId) => {
    setUploadProject(projId);
    setUploadTask('');
    if (projId) {
      try {
        const res = await api.get(`/wp/v2/tasks?project_id=${projId}&per_page=100`);
        setUploadTasks(res.data || []);
      } catch(e) { setUploadTasks([]); }
    } else { setUploadTasks([]); }
  };

  const handleUpload = async () => {
    if (!uploadFile) { setUploadError('Please select a file.'); return; }
    if (!uploadProject) { setUploadError('Please select a project to link this file to.'); return; }
    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      const res = await api.post('/wp/v2/media', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const mediaId = res.data.id;
      // Link to task if selected
      if (uploadTask) {
        const taskRes = await api.get(`/wp/v2/tasks/${uploadTask}`);
        const current = JSON.parse(taskRes.data.meta?.gpf_file_ids || '[]');
        await api.put(`/wp/v2/tasks/${uploadTask}`, {
          meta: { gpf_file_ids: JSON.stringify([...new Set([...current, mediaId])]) }
        });
      }
      // Reset and refresh
      setShowUpload(false);
      setUploadFile(null);
      setUploadProject('');
      setUploadTask('');
      setUploadTasks([]);
      fetchAll();
    } catch(e) {
      setUploadError('Upload failed. Please try again.');
      console.error(e);
    } finally { setUploading(false); }
  };

  const getProjectName = (id) => projects.find(p => p.id === parseInt(id))?.title?.rendered || '';
  const getTaskName    = (id) => allTasks.find(t => t.id === parseInt(id))?.title?.rendered  || '';

  const getMediaContext = (file) => {
    for (const task of allTasks) {
      const ids = JSON.parse(task.meta?.gpf_file_ids || '[]');
      if (ids.includes(file.id)) {
        const project = projects.find(p => p.id === task.meta?.gpf_project_id);
        return { projectName: project?.title?.rendered || '', taskName: task.title?.rendered || '' };
      }
    }
    return null;
  };

  const fileIcon = (mimeType = '') => {
    if (mimeType.startsWith('image/'))                               return { icon: '🖼', color: '#7c3aed' };
    if (mimeType.includes('pdf'))                                    return { icon: '📄', color: '#dc2626' };
    if (mimeType.includes('word') || mimeType.includes('document'))  return { icon: '📝', color: '#2563eb' };
    if (mimeType.includes('sheet') || mimeType.includes('excel'))    return { icon: '📊', color: '#16a34a' };
    if (mimeType.includes('presentation'))                           return { icon: '📊', color: '#ea580c' };
    return { icon: '📎', color: '#64748b' };
  };

  const formatDate = (str) => str ? new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // ── Editor ──
  const openNew = () => {
    setEditing({ isNew: true });
    setTitle('Untitled Document');
    setProjectId(''); setTaskId('');
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = '';
      editorRef.current?.focus();
    }, 50);
  };

  const openDoc = (doc) => {
    setEditing(doc);
    setTitle(doc.title?.rendered || 'Untitled');
    setProjectId(doc.meta?.gpf_project_id?.toString() || '');
    setTaskId(doc.meta?.gpf_task_id?.toString() || '');
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = doc.content?.rendered || '';
    }, 50);
  };

  const handleSave = useCallback(async (silent = false) => {
    if (!silent) setSaving(true);
    const content = editorRef.current?.innerHTML || '';
    try {
      const data = { title, content, project_id: parseInt(projectId) || 0, task_id: parseInt(taskId) || 0, author_id: currentUser.id || 0 };
      if (editing?.isNew) {
        const res = await createDocument(data);
        setEditing(res.data);
        await fetchAll();
      } else if (editing?.id) {
        await updateDocument(editing.id, data);
        await fetchAll();
      }
      setLastSaved(new Date());
      if (!silent) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (err) { console.error(err); if (!silent) setSaveError('Failed to save. Check console for details.'); }
    finally { if (!silent) setSaving(false); }
  }, [title, projectId, taskId, editing, currentUser.id]);

  useEffect(() => {
    const handler = (e) => {
      if (editing && (e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); handleSave(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editing, handleSave]);

  useEffect(() => {
    if (!editing || editing.isNew) return;
    const interval = setInterval(() => handleSave(true), 30000);
    return () => clearInterval(interval);
  }, [editing, handleSave]);

  const execCmd = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };

  const insertLink = () => {
    const url = window.prompt('Enter a URL:', 'https://');
    if (url) execCmd('createLink', url);
  };

  const handleDeleteDoc = async (id, title, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${title}"?`)) return;
    await deleteDocument(id);
    setDocs(docs.filter(d => d.id !== id));
    if (editing?.id === id) setEditing(null);
  };

  // ── Filtering ──
  const filteredDocs = docs.filter(d => !search || d.title?.rendered?.toLowerCase().includes(search.toLowerCase()));
  const filteredMedia = media.filter(m => !search || m.title?.rendered?.toLowerCase().includes(search.toLowerCase()));

  // ── EDITOR VIEW ──
  if (editing !== null) {
    return (
      <Layout>
        <div style={styles.editorPage}>
          <div style={styles.topBar}>
            <button style={styles.backBtn} onClick={() => setEditing(null)}>← Documents</button>
            <div style={styles.topCenter}>
              <input style={styles.titleInput} value={title} onChange={e => setTitle(e.target.value)} placeholder="Document title..." />
              {lastSaved && <span style={styles.autoSaveHint}>Auto-saved {lastSaved.toLocaleTimeString()}</span>}
            </div>
            <button style={{ ...styles.saveBtn, background: saved ? '#16a34a' : saving ? '#94a3b8' : '#2563eb' }} onClick={() => { setSaveError(''); handleSave(); }} disabled={saving}>
              {saving ? 'Saving...' : saved ? '✓ Saved' : '💾 Save'}
            </button>
          </div>
          {saveError && <p style={{ color: '#ef4444', fontSize: 12, margin: '0 0 8px' }}>{saveError}</p>}

          <div style={styles.linkBar}>
            <span style={styles.linkBarLabel}>🔗 Link to:</span>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>📁 Projects</span>
                <select style={styles.linkSelect} value={projectId} onChange={e => setProjectId(e.target.value)}>
                  <option value="">Select project...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.title?.rendered}</option>)}
                </select>
              </div>
              {projectId && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>✅ Tasks</span>
                  <select style={styles.linkSelect} value={taskId} onChange={e => setTaskId(e.target.value)}>
                    <option value="">Select task (optional)...</option>
                    {tasks.map(t => <option key={t.id} value={t.id}>{t.title?.rendered}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div style={styles.editorWrap}>
            <div style={styles.toolbar}>
              {TOOLBAR.map((item, i) => {
                if (item.sep) return <div key={i} style={styles.sep} />;

                if (item.type === 'link') {
                  return (
                    <button key={i} type="button" style={{ ...styles.toolBtn, ...item.style }} onMouseDown={e => { e.preventDefault(); insertLink(); }} title={item.title}>
                      {item.icon}
                    </button>
                  );
                }
                if (item.type === 'color') {
                  return (
                    <label key={i} style={{ ...styles.toolBtn, ...item.style, position: 'relative', overflow: 'hidden' }} title={item.title}>
                      {item.icon}
                      <input type="color" defaultValue="#000000" style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} onChange={e => execCmd('foreColor', e.target.value)} />
                    </label>
                  );
                }
                if (item.type === 'highlight') {
                  return (
                    <label key={i} style={{ ...styles.toolBtn, ...item.style, position: 'relative', overflow: 'hidden' }} title={item.title}>
                      {item.icon}
                      <input type="color" defaultValue="#fff3a3" style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} onChange={e => execCmd('hiliteColor', e.target.value)} />
                    </label>
                  );
                }
                return (
                  <button key={i} type="button" style={{ ...styles.toolBtn, ...item.style }}
                    onMouseDown={e => { e.preventDefault(); execCmd(item.cmd, item.val || null); }}
                    title={item.title}>{item.icon}</button>
                );
              })}
            </div>
            <div ref={editorRef} contentEditable suppressContentEditableWarning className="gpf-doc-editor" style={styles.editor} />
          </div>
        </div>
      </Layout>
    );
  }

  // ── LIST VIEW ──
  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Documents</h1>
            <p style={styles.subtitle}>{docs.length} created · {media.length} uploaded ({media.filter(m => !getMediaContext(m)).length} unlinked)</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{ ...styles.primaryBtn, background: '#16a34a' }} onClick={() => { setShowUpload(true); setUploadError(''); }}>
              📎 Upload File
            </button>
            <button style={styles.primaryBtn} onClick={openNew}>+ New Document</button>
          </div>
        </div>

        <div style={styles.controlBar}>
          <input style={styles.searchInput} placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} />
          <div style={styles.tabs}>
            {[{ key: 'all', label: 'All' }, { key: 'created', label: 'Created' }, { key: 'uploaded', label: 'Uploaded Files' }].map(t => (
              <button key={t.key} style={{ ...styles.tab, ...(tab === t.key ? styles.tabActive : {}) }} onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>
        </div>

        {loading ? <p style={styles.loading}>Loading...</p> : (
          <div style={styles.docGrid}>

            {/* ── Created documents ── */}
            {(tab === 'all' || tab === 'created') && (
              <>
                {tab === 'all' && filteredDocs.length > 0 && <div style={styles.sectionLabel}>📝 Created Documents</div>}
                {filteredDocs.length === 0 && tab === 'created' && (
                  <div style={styles.empty}>
                    <p style={styles.emptyTitle}>No created documents yet.</p>
                    <button style={styles.primaryBtn} onClick={openNew}>Create your first document</button>
                  </div>
                )}
                {filteredDocs.map(doc => (
                  <div key={`doc-${doc.id}`} style={styles.docCard} onClick={() => openDoc(doc)}>
                    <div style={styles.docIconBox}>📝</div>
                    <div style={styles.docInfo}>
                      <h3 style={styles.docTitle}>{doc.title?.rendered || 'Untitled'}</h3>
                      <p style={styles.docSnippet}>{doc.content?.rendered?.replace(/<[^>]+>/g, '').slice(0, 100) || 'No content'}</p>
                      <div style={styles.docMeta}>
                        {doc.meta?.gpf_project_id > 0 && <span style={styles.projectBadge}>{getProjectName(doc.meta.gpf_project_id).toUpperCase()}</span>}
                        {doc.meta?.gpf_task_id    > 0 && <span style={styles.taskBadge}>→ {getTaskName(doc.meta.gpf_task_id)}</span>}
                        <span style={styles.metaText}>by {doc.gpf_author_name || 'Unknown'}</span>
                        <span style={styles.metaText}>{formatDate(doc.modified)}</span>
                      </div>
                    </div>
                    <button style={styles.deleteBtn} onClick={e => handleDeleteDoc(doc.id, doc.title?.rendered, e)} title="Delete">✕</button>
                  </div>
                ))}
              </>
            )}

            {/* ── Uploaded files ── */}
            {(tab === 'all' || tab === 'uploaded') && (
              <>
                {tab === 'all' && filteredMedia.length > 0 && <div style={styles.sectionLabel}>📎 Uploaded Files</div>}
                {filteredMedia.length === 0 && tab === 'uploaded' && (
                  <div style={styles.empty}><p style={styles.emptyTitle}>No uploaded files yet.</p></div>
                )}
                {filteredMedia.map(file => {
                  const { icon, color } = fileIcon(file.mime_type);
                  const ctx = getMediaContext(file);
                  const isImage = file.mime_type?.startsWith('image/');
                  return (
                    <div key={`media-${file.id}`} style={styles.docCard}>
                      <div style={{ ...styles.docIconBox, background: color + '15', color, fontSize: 22 }}>
                        {isImage && file.source_url ? (
                          <img src={file.source_url} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }} />
                        ) : icon}
                      </div>
                      <div style={styles.docInfo}>
                        <a href={file.source_url} target="_blank" rel="noreferrer" style={{ ...styles.docTitle, textDecoration: 'none', color: '#0f172a' }}>
                          {file.title?.rendered || file.slug || 'File'}
                        </a>
                        <div style={styles.docMeta}>
                          <span style={{ ...styles.metaText, fontWeight: 600, color }}>{file.mime_type?.split('/')[1]?.toUpperCase()}</span>
                          <span style={styles.metaText}>{formatSize(file.media_details?.filesize)}</span>
                          {ctx?.projectName && <span style={styles.projectBadge}>{ctx.projectName.toUpperCase()}</span>}
                          {ctx?.taskName    && <span style={styles.taskBadge}>→ {ctx.taskName}</span>}
                          {!ctx && <span style={{ fontSize: 11, color: '#dc2626', fontStyle: 'italic', background: '#fef2f2', padding: '1px 6px', borderRadius: 4 }}>⚠ Not linked</span>}
                          <span style={styles.metaText}>{formatDate(file.date)}</span>
                        </div>
                      </div>
                      <a href={file.source_url} target="_blank" rel="noreferrer" style={styles.downloadBtn} title="Download">↓</a>
                    </div>
                  );
                })}
              </>
            )}

            {/* Empty state */}
            {tab === 'all' && filteredDocs.length === 0 && filteredMedia.length === 0 && (
              <div style={styles.empty}>
                <p style={styles.emptyTitle}>{search ? 'No documents match your search.' : 'No documents yet.'}</p>
                {!search && <button style={styles.primaryBtn} onClick={openNew}>Create your first document</button>}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Upload File Modal */}
      {showUpload && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 32, width: '100%', maxWidth: 460, boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>📎 Upload File</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>Files must be linked to a project or task.</p>

            {/* File picker */}
            <div style={{ marginBottom: 14 }}>
              <label style={styles.formLabel}>Select File *</label>
              <input
                type="file"
                style={{ width: '100%', padding: '8px 0', fontSize: 13 }}
                onChange={e => setUploadFile(e.target.files[0])}
              />
              {uploadFile && <p style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>✓ {uploadFile.name}</p>}
            </div>

            {/* Project selector */}
            <div style={{ marginBottom: 14 }}>
              <label style={styles.formLabel}>📁 Projects *</label>
              <select
                style={styles.formSelect}
                value={uploadProject}
                onChange={e => handleUploadProjectChange(e.target.value)}
              >
                <option value="">Select a project...</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.title?.rendered}</option>)}
              </select>
            </div>

            {/* Task selector */}
            {uploadProject && (
              <div style={{ marginBottom: 14 }}>
                <label style={styles.formLabel}>✅ Tasks (optional)</label>
                <select
                  style={styles.formSelect}
                  value={uploadTask}
                  onChange={e => setUploadTask(e.target.value)}
                >
                  <option value="">No specific task</option>
                  {uploadTasks.map(t => <option key={t.id} value={t.id}>{t.title?.rendered}</option>)}
                </select>
              </div>
            )}

            {uploadError && <p style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{uploadError}</p>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button
                style={{ padding: '9px 20px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 14 }}
                onClick={() => { setShowUpload(false); setUploadFile(null); setUploadProject(''); setUploadTask(''); }}
              >Cancel</button>
              <button
                style={{ padding: '9px 20px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}
                onClick={handleUpload}
                disabled={uploading}
              >{uploading ? 'Uploading...' : 'Upload File'}</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  page:        { maxWidth: 1000 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:    { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  formLabel:   { display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' },
  formSelect:  { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, outline: 'none', background: 'white' },
  primaryBtn:  { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  controlBar:  { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  searchInput: { padding: '9px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', width: 260 },
  tabs:        { display: 'flex', gap: 4, background: '#fff', padding: 4, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tab:         { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: 'transparent', color: '#64748b' },
  tabActive:   { background: '#2563eb', color: '#fff' },
  loading:     { color: '#64748b', fontSize: 14 },
  docGrid:     { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionLabel:{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 0 4px' },
  docCard:     { display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', cursor: 'pointer' },
  docIconBox:  { width: 40, height: 40, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 },
  docInfo:     { flex: 1, minWidth: 0 },
  docTitle:    { fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 4px', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  docSnippet:  { fontSize: 12, color: '#94a3b8', margin: '0 0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  docMeta:     { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  projectBadge:{ fontSize: 11, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '2px 8px', borderRadius: 99 },
  taskBadge:   { fontSize: 11, color: '#64748b' },
  metaText:    { fontSize: 11, color: '#94a3b8' },
  deleteBtn:   { background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 14, flexShrink: 0, padding: '0 4px' },
  downloadBtn: { width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', textDecoration: 'none', fontSize: 14, flexShrink: 0 },
  empty:       { background: '#fff', borderRadius: 10, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyTitle:  { color: '#64748b', fontSize: 14, marginBottom: 16 },
  editorPage:  { maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 12 },
  topBar:      { display: 'flex', alignItems: 'center', gap: 12 },
  backBtn:     { background: 'none', border: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' },
  topCenter:   { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  titleInput:  { fontSize: 20, fontWeight: 700, border: 'none', outline: 'none', color: '#0f172a', background: 'transparent', fontFamily: 'inherit' },
  autoSaveHint:{ fontSize: 11, color: '#94a3b8' },
  saveBtn:     { padding: '8px 20px', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.2s' },
  linkBar:     { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  linkBarLabel:{ fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' },
  linkSelect:  { padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', minWidth: 200 },
  editorWrap:  { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  toolbar:     { display: 'flex', alignItems: 'center', gap: 2, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', background: '#fafafa', borderRadius: '10px 10px 0 0' },
  toolBtn:     { padding: '4px 8px', border: 'none', borderRadius: 5, background: 'none', cursor: 'pointer', fontSize: 13, color: '#374151', minWidth: 28, textAlign: 'center' },
  sep:         { width: 1, height: 20, background: '#e2e8f0', margin: '0 4px' },
  editor:      { padding: '32px 40px', fontSize: 15, lineHeight: 1.8, color: '#1e293b', outline: 'none', minHeight: 500, fontFamily: "'Georgia', serif" },
};